import React, { useCallback, useMemo } from "react";
import { Box, Button, HStack, Icon, Image } from "@chakra-ui/react";
import {
  useAssistance,
  useCameras,
  useChimeMeeting,
  usePlugin,
  useView,
} from "../../../hooks";
import {
  CompactPluginIcon,
  FullPluginIcon,
  LargePluginIcon,
  StandardPluginIcon,
} from "../icons";
import { PluginView } from "../../../enums";
import ViewButton from "../buttons/ViewButton";
import LogoIcon from "../icons/LogoIcon";
import AssistXRPlugin from "../../../../assets/images/Assist XR_plugin.png"

/**
 * Renders the plugin header

* @returns The plugin header
 */
const PluginHeader = () => {
  const { assistance } = useAssistance();
  const { hasJoined } = useChimeMeeting();
  const { onPluginOpen } = usePlugin();
  const { isChatOpen, toggleChat, pluginView, hasOpenLargeView, onPluginViewChange } = useView();
  const { onSelectExteriorCameras } = useCameras();

  const isStandardViewSelected = useMemo(
    () => pluginView === PluginView.STANDARD,
    [pluginView],
  );
  const isLargeViewSelected = useMemo(
    () => pluginView === PluginView.LARGE && !isChatOpen,
    [pluginView, isChatOpen],
  );
  const isFullViewSelected = useMemo(
    () => pluginView === PluginView.LARGE && isChatOpen,
    [pluginView, isChatOpen],
  );

  /**
   * Handles the compact view click event
   */
  const onCompactViewClick = useCallback(() => {
    onPluginViewChange(PluginView.COMPACT);
  }, [onPluginViewChange]);

  /**
   * Handles the standard view click event
   */
  const onStandardViewClick = useCallback(() => {
    onPluginViewChange(PluginView.STANDARD);
  }, [onPluginViewChange]);

  /**
   * Handles the large view click event
   */
  const onLargeViewClick = useCallback(() => {
    if (!hasOpenLargeView) {
      onSelectExteriorCameras();
    }
    onPluginViewChange(PluginView.LARGE);
  }, [
    hasOpenLargeView,
    onPluginViewChange,
    onSelectExteriorCameras,
  ]);

  /**
   * Handles the full view click event
   */
  const onFullViewClick = useCallback(() => {
    if (!hasOpenLargeView) {
      onSelectExteriorCameras();
    }
    if (assistance?.category === "ecall") {
      onPluginViewChange(PluginView.FULL);
    } else {
      onPluginViewChange(PluginView.LARGE);
      if (!isChatOpen) {
        toggleChat();
      }
    }
  }, [
    assistance,
    isChatOpen,
    toggleChat,
    hasOpenLargeView,
    onPluginViewChange,
    onSelectExteriorCameras,
  ]);

  return pluginView === PluginView.COMPACT ? (
    <Box margin={"16px"}>
      <Button
        boxSize={20}
        id={"plugin-button"}
        isDisabled={!assistance && !hasJoined}
        onClick={onPluginOpen}
        borderRadius={"7px"}
        backgroundColor={"transparent"}
        bgGradient={"linear(135deg, #BFBFBF77, #BFBFBF11)"}
        borderWidth={"1px"}
        borderColor={"#FFFFFF55"}
        color={"white"}
        _hover={{
          color: "#82e600"
        }}
        _active={{
          color: "#82e600"
        }}
      >
        <Icon as={LogoIcon} boxSize={16} />
      </Button>
    </Box>
  ) : (
    <HStack justifyContent={"space-between"}>
      <Box>
        <HStack gap={"16px"}>
          <Image src={AssistXRPlugin} height={"32px"} />
          {/* <Heading fontSize={"24px"} fontWeight={500}>Assist XR</Heading>
          <Heading fontSize={"24px"} fontWeight={300}>Plugin</Heading> */}
        </HStack>
      </Box>
      <HStack>
        <ViewButton
          icon={CompactPluginIcon}
          onClick={onCompactViewClick}
          width={"50px"}
        />
        <ViewButton
          icon={StandardPluginIcon}
          isSelected={isStandardViewSelected}
          onClick={onStandardViewClick}
          width={"50px"}
        />
        <ViewButton
          icon={LargePluginIcon}
          isSelected={isLargeViewSelected}
          onClick={onLargeViewClick}
          width={"50px"}
        />
        <ViewButton
          icon={FullPluginIcon}
          isSelected={isFullViewSelected}
          onClick={onFullViewClick}
          width={"50px"}
        />
      </HStack>
    </HStack>
  );
};

export default React.memo(PluginHeader);
